import { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import {
  AiClipperMetricsSummary,
  AiClipCandidateApprovalSource,
  AiClipperSourceType,
  AiClipperTimeSeriesGranularity,
  PostCandidateStatus,
} from 'api/aiClipperMetrics/types';

export const DEFAULT_PAGE_SIZE = 20;
export const ATHLETE_DEBOUNCE_MS = 350;
// Mirrors the detection in pages/AIClips/index.tsx — slug-like strings get
// routed to athleteSlug (exact), otherwise to athleteName (ILIKE).
export const SLUG_LIKE_REGEX = /^[a-z0-9]+(?:[.\-_][a-z0-9]+)+$/i;

export const STATUS_OPTIONS: { value: PostCandidateStatus; label: string }[] = [
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'NotReviewed', label: 'Pending' },
];

export const EMPTY_SUMMARY: AiClipperMetricsSummary = {
  totalGenerated: 0,
  accepted: 0,
  manualAccepted: 0,
  systemAccepted: 0,
  rejected: 0,
  notReviewed: 0,
  acceptRatePctOfReviewed: null,
  sourceBreakdown: [],
};

export const SOURCE_TYPE_OPTIONS: {
  value: AiClipperSourceType;
  label: string;
}[] = [
  { value: 'Interview', label: 'Interview' },
  { value: 'Post', label: 'Post' },
];

export const INTERVIEW_METRICS_SOURCE_TYPES: AiClipperSourceType[] = [
  'Interview',
  'Post',
];

export const SOURCE_TYPE_LABELS: Record<AiClipperSourceType, string> = {
  Interview: 'Interview',
  Post: 'Post',
  PpvStream: 'PPV Stream',
};

/** Falls back to the raw enum name so a source type the FE does not know yet is not a blank cell. */
export function sourceTypeLabel(value: AiClipperSourceType | string): string {
  return SOURCE_TYPE_LABELS[value as AiClipperSourceType] ?? value;
}

export function withInterviewMetricsSourceTypes(
  selected: AiClipperSourceType[]
): AiClipperSourceType[] {
  const allowed = selected.filter(
    (source) => source === 'Interview' || source === 'Post'
  );
  return allowed.length > 0 ? allowed : INTERVIEW_METRICS_SOURCE_TYPES;
}

export const APPROVAL_SOURCE_OPTIONS: {
  value: AiClipCandidateApprovalSource;
  label: string;
}[] = [
  { value: 'Manual', label: 'Manual' },
  { value: 'System', label: 'System' },
];

export const TIME_SERIES_BUCKET_HINT =
  'Accepted clips bucket by approval date; pending and rejected bucket by creation date.';

export type DateRange = [Moment | null, Moment | null] | null;

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function formatRatePct(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function formatBucketLabel(
  iso: string,
  granularity: AiClipperTimeSeriesGranularity
): string {
  const d = moment(iso);
  if (granularity === 'Year') return d.format('YYYY');
  if (granularity === 'Month') return d.format("MMM 'YY");
  if (granularity === 'Day') return d.format('MMM D');
  return d.format('MMM D');
}

export const GRANULARITY_ALIGNMENT: Record<
  AiClipperTimeSeriesGranularity,
  string
> = {
  Day: 'day',
  Week: 'Monday',
  Month: '1st of month',
  Year: '1st of year',
};

export const CHART_SERIES: {
  dataKey: 'Manual approvals' | 'System approvals' | 'Total approvals';
  stroke: string;
}[] = [
  { dataKey: 'Total approvals', stroke: '#1677ff' },
  { dataKey: 'Manual approvals', stroke: '#16a34a' },
  { dataKey: 'System approvals', stroke: '#475569' },
];

// Wraps a loader so each one doesn't have to repeat the
// setLoading/try/catch/setError plumbing.
export async function runLoad<T>(
  setLoading: (b: boolean) => void,
  setError: (msg: string | null) => void,
  fallbackMessage: string,
  fn: () => Promise<T>
): Promise<T | undefined> {
  setLoading(true);
  try {
    return await fn();
  } catch (e: any) {
    setError(e?.message || fallbackMessage);
    return undefined;
  } finally {
    setLoading(false);
  }
}
